// Layout · Stack
//
// Flex-column container. Closed-enum `gap` over the shared spacing scale.
// No padding, no border, no `style`. Wrap in `Box` when a surface is needed.

import { Children, Fragment, type ReactNode } from "react";
import { Hairline } from "@ds/display/Hairline";
import { GAP_TO_SPACE, type Gap } from "@ds/layout/Stack.helpers";

export type { Gap } from "@ds/layout/Stack.helpers";

export type StackAlign   = "start" | "center" | "end" | "stretch";
export type StackJustify = "start" | "center" | "end" | "between" | "around" | "evenly";

/** Hairline divider between children. Maps to `SURFACE.border` ("hairline") or `SURFACE.borderSoft` ("soft"). */
export type StackDivider = "hairline" | "soft";

export interface StackInput {
  children: ReactNode;
  /** Gap between children. Default `"md"`. */
  gap?: Gap;
  /** Cross-axis (horizontal) alignment. */
  align?: StackAlign;
  /** Main-axis (vertical) alignment. */
  justify?: StackJustify;
  fullHeight?: boolean;
  fullWidth?: boolean;
  /**
   * Render a horizontal hairline between consecutive children.
   * - `"hairline"` → `1px solid SURFACE.border` — section frames, card dividers.
   * - `"soft"` → `1px solid SURFACE.borderSoft` — dense peer rows where a Card is too heavy.
   * Hairlines sit at the gap midpoint; the gap rhythm is preserved.
   */
  divider?: StackDivider;
  testId?: string;
  className?: string;
}

export type StackProps = StackInput;

function alignToCss(a: StackAlign): string {
  if (a === "start")  return "flex-start";
  if (a === "end")    return "flex-end";
  return a;
}

function justifyToCss(j: StackJustify): string {
  if (j === "start")   return "flex-start";
  if (j === "end")     return "flex-end";
  if (j === "between") return "space-between";
  if (j === "around")  return "space-around";
  if (j === "evenly")  return "space-evenly";
  return j;
}

const DIVIDER_VARIANT: Record<StackDivider, "default" | "soft"> = {
  hairline: "default",
  soft:     "soft",
};

export function Stack({
  children,
  gap = "md",
  align,
  justify,
  fullHeight,
  fullWidth,
  divider,
  testId,
  className,
}: StackProps) {
  const style: Record<string, unknown> = {
    display: "flex",
    flexDirection: "column",
    gap: GAP_TO_SPACE[gap],
    minWidth: 0,
  };
  if (align)      style.alignItems     = alignToCss(align);
  if (justify)    style.justifyContent = justifyToCss(justify);
  if (fullHeight) style.height         = "100%";
  if (fullWidth)  style.width          = "100%";

  const childArray = Children.toArray(children);
  const resolvedChildren = divider
    ? childArray.flatMap((child, i) =>
        i < childArray.length - 1
          ? [child, <Fragment key={`divider-${i}`}><Hairline variant={DIVIDER_VARIANT[divider]} /></Fragment>]
          : [child]
      )
    : children;

  return (
    <div
      data-testid={testId}
      className={className}
      style={style}
    >
      {resolvedChildren}
    </div>
  );
}
